import type { IRowNode, PostSortRowsParams } from "ag-grid-enterprise";
import type {
  DesignScopeCatalogItem,
  DesignScopeCatalogItemClassification
} from "./apis";

export type CatalogTreeSystem = "UF" | "MF";

export const CATALOG_TREE_SYSTEM_LABEL: Record<CatalogTreeSystem, string> = {
  UF: "UniFormat",
  MF: "MasterFormat"
};

const SEG_SEP = "|";
export const CATALOG_UNCLASSIFIED = "__unclassified__";

const storedSystem = (
  system: CatalogTreeSystem
): DesignScopeCatalogItemClassification["system"] =>
  system === "UF" ? "UNIFORMAT" : "MASTERFORMAT";

export const encodeCatalogSeg = (parts: {
  system: CatalogTreeSystem;
  code: string;
  title: string;
}) => [parts.system, parts.code, parts.title].join(SEG_SEP);

export const decodeCatalogSeg = (
  seg: string
): { system: CatalogTreeSystem; code: string; title: string } => {
  const [system, code, ...rest] = seg.split(SEG_SEP);
  return {
    system: (system as CatalogTreeSystem) || "UF",
    code: code ?? "",
    title: rest.join(SEG_SEP)
  };
};

export function classificationForTreeSystem(
  item: DesignScopeCatalogItem,
  system: CatalogTreeSystem
): DesignScopeCatalogItemClassification | null {
  return (
    item.classifications.find((c) => c.system === storedSystem(system)) ?? null
  );
}

/**
 * Ancestor group path for AG Grid treeData. Uses stored `path` (root first);
 * falls back to the leaf code when path is missing. When path omits the item's
 * own code (API often returns ancestors only), appends that leaf classification
 * as a group so siblings share a parent. Then appends `item.id` as the row leaf.
 */
export function catalogItemDataPath(
  item: DesignScopeCatalogItem,
  system: CatalogTreeSystem
): string[] {
  const hit = classificationForTreeSystem(item, system);
  if (!hit?.code) return [CATALOG_UNCLASSIFIED, item.id];
  const nodes =
    hit.path && hit.path.length > 0
      ? [...hit.path]
      : [{ number: hit.code, title: hit.name }];
  const last = nodes[nodes.length - 1];
  if (!last || last.number !== hit.code) {
    nodes.push({ number: hit.code, title: hit.name });
  }
  return [
    ...nodes.map((node) =>
      encodeCatalogSeg({
        system,
        code: node.number,
        title: node.title
      })
    ),
    item.id
  ];
}

/** Direct leaf items first, then nested classification groups (matches DSM log). */
export function placeCatalogItemsBeforeNestedGroups<TData>(
  params: PostSortRowsParams<TData>
) {
  const leaves: IRowNode<TData>[] = [];
  const groups: IRowNode<TData>[] = [];
  for (const node of params.nodes) {
    if (node.group) groups.push(node);
    else leaves.push(node);
  }
  params.nodes.splice(0, params.nodes.length, ...leaves, ...groups);
}
